import { useState } from "react";
import { Link } from "react-router-dom";
import { influencers, resellerBenefits, platformList } from "@/mocks/influencers";

const platformColors: Record<string, string> = {
  TikTok: "#010101",
  YouTube: "#FF0000",
  Instagram: "#E1306C",
  Douyin: "#010101",
  WeChat: "#07C160",
};

const platformIcons: Record<string, string> = {
  TikTok: "ri-tiktok-line",
  YouTube: "ri-youtube-line",
  Instagram: "ri-instagram-line",
  Douyin: "ri-tiktok-line",
  WeChat: "ri-wechat-line",
};

const InfluencerSection = () => {
  const [activeTab, setActiveTab] = useState("All");

  const filtered = activeTab === "All"
    ? influencers
    : influencers.filter((inf) => inf.platform === activeTab);

  return (
    <section id="influencers" className="py-16 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] text-xs font-semibold px-3 py-1 rounded-full mb-3">
            <i className="ri-star-line"></i> FOR RESELLERS &amp; INFLUENCERS
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
            TikTok · Instagram · YouTube Stars
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm">
            Earn real commission promoting USA &amp; Canada factory and warehouse products. Each factory sets their own commission rate. Get paid via PayPal every month.
          </p>
        </div>

        {/* Commission Highlight Banner */}
        <div className="bg-gradient-to-r from-[#1a0a0a] to-[#C41E3A] rounded-xl p-6 mb-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-wrap gap-6 items-center">
            {[
              { icon: "ri-percent-line", label: "Factory Commission", value: "5% – 40%" },
              { icon: "ri-paypal-line", label: "Payout Method", value: "PayPal" },
              { icon: "ri-bar-chart-2-line", label: "Track Sales", value: "Real-Time" },
              { icon: "ri-truck-line", label: "Dropship From", value: "USA & Canada" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg">
                  <i className={`${item.icon} text-[#D4AF37] text-lg`}></i>
                </div>
                <div>
                  <div className="text-white/60 text-[10px]">{item.label}</div>
                  <div className="text-white font-bold text-sm">{item.value}</div>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/reseller/login"
            className="flex items-center gap-2 bg-[#D4AF37] text-[#1a0a0a] px-6 py-3 rounded-full font-bold text-sm hover:bg-[#c9a227] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-vip-crown-line"></i>
            Join as Reseller
          </Link>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-12 mb-14">
          {/* Benefits */}
          <div className="lg:w-2/5">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Why Join Our Reseller Program?</h3>
            <div className="space-y-4">
              {resellerBenefits.map((b) => (
                <div key={b.title} className="flex gap-4 items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#C41E3A]/10 rounded-lg flex-shrink-0">
                    <i className={`${b.icon} text-[#C41E3A] text-lg`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{b.title}</div>
                    <div className="text-gray-500 text-xs mt-1 leading-relaxed">{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/reseller/login"
                className="flex items-center gap-2 bg-[#C41E3A] text-white px-5 py-3 rounded-full font-semibold text-sm hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-vip-crown-line"></i> Reseller Dashboard
              </Link>
              <a
                href="https://wa.me/1234567890"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 rounded-full font-semibold text-sm hover:bg-[#1ebe5d] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i> Join via WhatsApp
              </a>
            </div>
          </div>

          {/* Stats */}
          <div className="lg:w-3/5">
            <div className="grid grid-cols-2 gap-4 mb-6">
              {[
                { label: "Active Resellers", value: "12,400+", icon: "ri-user-star-line" },
                { label: "Avg. Monthly Earnings", value: "$4,200", icon: "ri-money-dollar-circle-line" },
                { label: "Products Available", value: "50,000+", icon: "ri-store-2-line" },
                { label: "PayPal Payouts Sent", value: "$2.4M+", icon: "ri-paypal-line" },
              ].map((stat) => (
                <div key={stat.label} className="bg-[#fdf8f8] rounded-lg p-5 border border-gray-100">
                  <div className="w-8 h-8 flex items-center justify-center mb-2">
                    <i className={`${stat.icon} text-[#C41E3A] text-xl`}></i>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">{stat.value}</div>
                  <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B0000] rounded-lg p-6 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-[#D4AF37] text-2xl"></i>
                </div>
                <div className="font-bold text-lg">Crown Reseller Program</div>
              </div>
              <p className="text-white/80 text-sm mb-4">
                Each USA &amp; Canada factory decides their own commission %. You see the rate before promoting. Track every sale and commission in your dashboard. Paid to PayPal monthly.
              </p>
              <div className="flex flex-wrap gap-2">
                {["TikTok Shop", "Instagram Affiliate", "YouTube Deals"].map((p) => (
                  <span key={p} className="bg-white/20 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">{p}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Platform Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {platformList.map((p) => (
            <button
              key={p}
              onClick={() => setActiveTab(p)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === p
                  ? "bg-[#C41E3A] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {p}
            </button>
          ))}
        </div>

        {/* Influencer Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filtered.map((inf) => (
            <div key={inf.id} className="bg-white border border-gray-100 rounded-lg p-5 hover:border-[#C41E3A] transition-all cursor-pointer group">
              <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                  <img
                    src={inf.avatar}
                    alt={inf.name}
                    className="w-14 h-14 rounded-full object-cover object-top border-2 border-[#D4AF37]"
                  />
                  <div
                    className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px]"
                    style={{ backgroundColor: platformColors[inf.platform] || "#333" }}
                  >
                    <i className={platformIcons[inf.platform] || "ri-share-line"}></i>
                  </div>
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-sm">{inf.name}</div>
                  <div className="text-xs text-gray-400">{inf.handle}</div>
                </div>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Platform</span>
                  <span className="font-semibold text-gray-700">{inf.platform}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Followers</span>
                  <span className="font-semibold text-gray-700">{inf.followers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Niche</span>
                  <span className="font-semibold text-gray-700">{inf.niche}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Avg. Earnings</span>
                  <span className="font-bold text-[#C41E3A]">{inf.earnings}</span>
                </div>
              </div>
              <Link
                to="/reseller/login"
                className="mt-4 block w-full bg-[#C41E3A]/10 text-[#C41E3A] text-xs py-2 rounded-md font-semibold hover:bg-[#C41E3A] hover:text-white transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                Join &amp; Earn Commission
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InfluencerSection;
